import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { WebSocketProvider } from "./contexts/WebSocketContext";

// Pages
import { LoginPage } from "./pages/LoginPage";
import { AdminDashboard } from "./pages/admin/AdminDashboard";
import { RaceManagement } from "./pages/admin/RaceManagement";
import { UserManagement } from "./pages/admin/UserManagement";
import { ExposureView } from "./pages/admin/ExposureView";
import { BetsReport } from "./pages/admin/BetsReport";
import { Settlement } from "./pages/admin/Settlement";
import { SystemSettings } from "./pages/admin/SystemSettings";
import { TraderDashboard } from "./pages/trader/TraderDashboard";
import { MyBets } from "./pages/trader/MyBets";
import { TraderBetsReport } from "./pages/trader/TraderBetsReport";
import { AdminChangePassword } from "./pages/admin/AdminChangePassword";
import { TraderChangePassword } from "./pages/trader/TraderChangePassword";

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/trader'} replace />;
  }

  return children;
}

// Auth Redirect Component
function AuthRedirect() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/trader'} replace />;
  }

  return <LoginPage />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<AuthRedirect />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/races" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <RaceManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <UserManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/exposure" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ExposureView />
        </ProtectedRoute>
      } />
      <Route path="/admin/bets" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <BetsReport />
        </ProtectedRoute>
      } />
      <Route path="/admin/settlement" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Settlement />
        </ProtectedRoute>
      } />
      <Route path="/admin/system" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <SystemSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/password" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminChangePassword />
        </ProtectedRoute>
      } />
      
      {/* Trader Routes */}
      <Route path="/trader" element={
        <ProtectedRoute allowedRoles={['trader']}>
          <TraderDashboard />
        </ProtectedRoute>
      } />
      <Route path="/trader/bets" element={
        <ProtectedRoute allowedRoles={['trader']}>
          <MyBets />
        </ProtectedRoute>
      } />
      <Route path="/trader/report" element={
        <ProtectedRoute allowedRoles={['trader']}>
          <TraderBetsReport />
        </ProtectedRoute>
      } />
      <Route path="/trader/password" element={
        <ProtectedRoute allowedRoles={['trader']}>
          <TraderChangePassword />
        </ProtectedRoute>
      } />
      
      {/* Default Redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

const isStaging =
  process.env.REACT_APP_IS_STAGING === "true" ||
  (typeof window !== "undefined" &&
    (window.location.port === "3001" ||
      window.location.host.endsWith(":3001") ||
      window.location.hostname.includes("staging")));

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WebSocketProvider>
          {isStaging && (
            <div
              className="fixed top-2 left-1/2 -translate-x-1/2 z-[100] px-3 py-1.5 rounded-md bg-red-500 text-white text-sm font-bold shadow-lg border border-red-600"
              aria-label="Test server"
            >
              Test server
            </div>
          )}
          <AppRoutes />
          <Toaster 
            position="top-right" 
            theme="dark"
            toastOptions={{
              style: {
                background: '#18181B',
                border: '1px solid #27272A',
                color: '#FAFAFA'
              }
            }}
          />
        </WebSocketProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
